import React from 'react';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const Contact = () => {
  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      detail: '416-747-6800',
      link: 'tel:416-747-6800',
      linkText: 'Call Now'
    },
    {
      icon: Mail,
      title: 'Email',
      detail: '7476800@gmail.com',
      link: 'mailto:7476800@gmail.com',
      linkText: 'Send Email'
    },
    {
      icon: MapPin,
      title: 'Location',
      detail: '21 Millwick Dr, North York ON M9L 1Y4, Canada',
      link: 'https://www.google.com/maps/search/?api=1&query=21+Millwick+Dr+North+York+ON+M9L+1Y4+Canada',
      linkText: 'Get Directions'
    },
    {
      icon: Clock,
      title: 'Hours',
      detail: 'Monday - Saturday: 9AM - 6PM',
      detail2: 'Sunday: Closed',
      linkText: null
    }
  ];

  return (
    <section id="contact" className="relative py-24 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#ff6b35] font-semibold text-sm uppercase tracking-wider mb-3 block">
            Get In Touch
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Visit Us Today
          </h2>
          <div className="h-1 w-24 bg-[#ff6b35] mx-auto rounded-full"></div>
          <p className="text-gray-400 text-lg mt-6 max-w-2xl mx-auto">
            Have questions or need service? We're here to help. Contact us or visit our shop 
            conveniently located in North York, serving Toronto, Vaughan, Markham, Richmond Hill, 
            Brampton, and the entire GTA.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information Cards */}
          <div className="space-y-6">
            {contactInfo.map((info, index) => {
              const IconComponent = info.icon;
              return (
                <Card
                  key={index}
                  className="bg-gradient-to-br from-[#1a1a1a] to-[#151515] border border-white/5 hover:border-[#ff6b35]/30 transition-all duration-300"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#ff6b35]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-6 h-6 text-[#ff6b35]" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-white font-semibold mb-2">{info.title}</h3>
                        <p className="text-gray-400 mb-1">{info.detail}</p>
                        {info.detail2 && (
                          <p className="text-gray-400 mb-1">{info.detail2}</p>
                        )}
                        {info.linkText && (
                          <a
                            href={info.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#ff6b35] hover:text-[#e55a28] font-medium text-sm inline-flex items-center mt-2 transition-colors duration-200"
                          >
                            {info.linkText}
                            <span className="ml-1">→</span>
                          </a>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Google Maps Embed */}
          <div className="relative">
            <div className="rounded-2xl overflow-hidden border border-white/10 h-full min-h-[500px]">
              <iframe
                title="Premium Auto Repairs & Sales Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2881.9888374728246!2d-79.5404708!3d43.7407088!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x882b2f8f5f5f5f5f%3A0x5f5f5f5f5f5f5f5f!2s21%20Millwick%20Dr%2C%20North%20York%2C%20ON%20M9L%201Y4!5e0!3m2!1sen!2sca!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="grayscale opacity-80 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
              ></iframe>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center bg-gradient-to-br from-[#1a1a1a] to-[#151515] border border-white/5 rounded-2xl p-12">
          <h3 className="text-3xl font-bold text-white mb-4">
            Ready to Service Your Vehicle?
          </h3>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Contact us today for a free quote or to schedule your appointment. 
            We're ready to keep you on the road!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:416-747-6800"
              className="inline-flex items-center justify-center bg-[#ff6b35] hover:bg-[#e55a28] text-white font-semibold px-8 py-4 rounded-lg transition-colors duration-200"
            >
              <Phone className="w-5 h-5 mr-2" />
              Call: 416-747-6800
            </a>
            <a
              href="mailto:7476800@gmail.com"
              className="inline-flex items-center justify-center border-2 border-white/20 bg-white/5 hover:bg-white/10 hover:border-[#ff6b35] text-white font-semibold px-8 py-4 rounded-lg transition-all duration-200"
            >
              <Mail className="w-5 h-5 mr-2" />
              Email Us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
